import { FC } from 'react';

import { addDays, startOfDay } from 'date-fns';
import { DEFAULT_AUCTION_AUTO_STRATEGY_SETTINGS } from 'utils/constants/auction';
import { Settings } from 'utils/constants/dynamicSettings';
import { formatDate } from 'utils/date';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import auctionCampaignBiddingStrategyButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_bidding_strategy_button_click';
import auctionCampaignBiddingStrategyElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_bidding_strategy_element_shown';

import { useBiddingStrategyTags } from 'hooks/auction/useBiddingStrategyTags';
import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { AutoStrategyDetailsModal } from 'components/AuctionBuilder/BiddingStrategySelect/AutoStrategyDetailsModal/AutoStrategyDetailsModal';
import { AutoStrategyLabelTooltipContent } from 'components/AuctionBuilder/BiddingStrategySelect/AutoStrategyLabelTooltipContent/AutoStrategyLabelTooltipContent';
import { BiddingStrategyCard } from 'components/AuctionBuilder/BiddingStrategySelect/BiddingStrategyCard/BiddingStrategyCard';
import { AuctionAutoStrategySettings } from 'components/AuctionBuilder/types/auctionAutoStrategySettings';
import { checkIsCampaignStartDateLessThanToday } from 'components/AuctionBuilder/utils';
import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';

import {
    AuctionDataField,
    AuctionPeriodType,
    auctionSelector,
    auctionSelectorNonNullable,
    BiddingStrategy,
    clearAuctionErrors,
    resetDates,
    setBiddingStrategy,
    setEndDate,
    setSelectedPeriodType,
    setStartDate,
} from 'store/models/auctionData';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/AuctionBuilder/BiddingStrategySelect/BiddingStrategySelect.module.less';

const TrlKeys = {
    label: 'AuctionBuilder.BiddingStrategySelect.label',
    [BiddingStrategy.Auto]: {
        label: 'AuctionBuilder.BiddingStrategySelect.BiddingStrategyCard.auto.label',
        description: 'AuctionBuilder.BiddingStrategySelect.BiddingStrategyCard.auto.description',
        labelTooltipContent: null,
    },
    [BiddingStrategy.Manual]: {
        label: 'AuctionBuilder.BiddingStrategySelect.BiddingStrategyCard.manual.label',
        description: 'AuctionBuilder.BiddingStrategySelect.BiddingStrategyCard.manual.description',
        labelTooltipContent: null,
    },
};

export const BiddingStrategySelect: FC = () => {
    const biddingStrategy = useSelector(auctionSelectorNonNullable[AuctionDataField.BiddingStrategy]);
    const selectedPeriodType = useSelector(auctionSelectorNonNullable[AuctionDataField.SelectedPeriodType]);
    const isDatesChanged = useSelector(auctionSelectorNonNullable[AuctionDataField.IsDatesChanged]);
    const campaignStartDate = useSelector(auctionSelector.auctionCampaign)?.startAt;
    const vacancyId = useSelector(auctionSelector.vacancy)?.vacancyId;

    const trls = useTrls();
    const dispatch = useDispatch();
    const settings = useSettings();
    const [isDetailsVisible, setIsDetailsVisibleOn, setIsDetailsVisibleOff] = useOnOffState(false);

    const isCampaignStartDateLessThanToday = checkIsCampaignStartDateLessThanToday(campaignStartDate);
    const biddingStrategyTags = useBiddingStrategyTags();
    const autoStrategyBenefits = settings(Settings.AuctionAutoStrategySettings) as AuctionAutoStrategySettings | null;
    const { applicationsGrowthPercent, applicationsCostReductionPercent } = autoStrategyBenefits || {};

    const onBiddingStrategyChange = (newBiddingStrategy: BiddingStrategy) => {
        if (newBiddingStrategy === biddingStrategy) {
            return;
        }

        if (newBiddingStrategy === BiddingStrategy.Auto) {
            if (selectedPeriodType === AuctionPeriodType.Unlimited && !isCampaignStartDateLessThanToday) {
                dispatch([
                    setStartDate(formatDate(startOfDay(new Date()))),
                    setEndDate(formatDate(startOfDay(addDays(new Date(), 7)))),
                ]);
            }
        }

        if (newBiddingStrategy === BiddingStrategy.Manual) {
            if (isDatesChanged) {
                dispatch(setSelectedPeriodType(AuctionPeriodType.Manual));
            } else {
                dispatch(setSelectedPeriodType(AuctionPeriodType.Unlimited));

                if (isCampaignStartDateLessThanToday) {
                    dispatch(setEndDate(null));
                } else {
                    dispatch(resetDates(null));
                }
            }
        }

        dispatch([
            setBiddingStrategy(newBiddingStrategy),
            clearAuctionErrors(AuctionDataField.StartAt),
            clearAuctionErrors(AuctionDataField.EndAt),
        ]);

        auctionCampaignBiddingStrategyButtonClick({
            biddingStrategy: newBiddingStrategy,
            vacancyId: String(vacancyId),
        });
    };

    return (
        <ElementShownAnchor
            fn={auctionCampaignBiddingStrategyElementShown}
            vacancyId={String(vacancyId)}
            biddingStrategy={biddingStrategy}
        >
            <MagritteFormField
                label={trls(TrlKeys.label)}
                input={
                    <div className={styles.cardsWrapper}>
                        {Object.values(BiddingStrategy).map((key) => (
                            <BiddingStrategyCard
                                key={key}
                                checked={key === biddingStrategy}
                                label={trls(TrlKeys[key as BiddingStrategy].label)}
                                description={[trls(TrlKeys[key as BiddingStrategy].description)]}
                                tags={biddingStrategyTags[key as BiddingStrategy]}
                                labelTooltipContent={
                                    key === BiddingStrategy.Auto ? (
                                        <AutoStrategyLabelTooltipContent
                                            vacancyId={vacancyId}
                                            onDetailsButtonClick={setIsDetailsVisibleOn}
                                        />
                                    ) : (
                                        trls(TrlKeys[key as BiddingStrategy].labelTooltipContent) || undefined
                                    )
                                }
                                placement="builder"
                                onClick={() => onBiddingStrategyChange(key as BiddingStrategy)}
                                dataQa={`bidding-strategy-${key}`}
                            />
                        ))}
                    </div>
                }
            />
            <AutoStrategyDetailsModal
                isVisible={isDetailsVisible}
                onClose={setIsDetailsVisibleOff}
                applicationsGrowthPercent={
                    applicationsGrowthPercent ?? DEFAULT_AUCTION_AUTO_STRATEGY_SETTINGS.applicationsGrowthPercent
                }
                applicationsCostReductionPercent={
                    applicationsCostReductionPercent ??
                    DEFAULT_AUCTION_AUTO_STRATEGY_SETTINGS.applicationsCostReductionPercent
                }
            />
        </ElementShownAnchor>
    );
};
